import React from "react";
import { graphql } from "react-relay";
import type { OnChangeValue } from "react-select";

import { faCheck, faInfoCircle, faLink, faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faExclamationCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import Tippy from "@tippyjs/react";
import cx from "classnames";
import { compact, noop, uniqBy } from "lodash";

import { BillingContext } from "~/account/Billing/BillingContext";
import type { AttendeeAttrs } from "~/common/Attendees";
import recaptcha from "~/lib/recaptcha";
import { commitMutationPromise, type NotOther, useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { copyToClipboard } from "~/lib/utils";
import { useMeetingRecorderEnabled } from "~/meetingRecorder/featureFlag";
import { environment } from "~/models";
import { Button, Checkbox, toast } from "~/ui";
import { ButtonLink, Modal, TextArea } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import Selector from "~/ui/selectors/Selector";

import EmailListInput, { type Contact } from "../common/EmailListInput";

import type { SendNotesRecordingModal_updateRecordingVisibilityMutation as UpdateRecordingVisibilityMutation } from "~/__generated__/SendNotesRecordingModal_updateRecordingVisibilityMutation.graphql";
import type { SendNotesRecordingModalQuery } from "~/__generated__/SendNotesRecordingModalQuery.graphql";
import type { SendNotesRecordingModalSendEmailMutation as SendEmailMutation } from "~/__generated__/SendNotesRecordingModalSendEmailMutation.graphql";

import styles from "./css/SendNotesRecordingModal.module.css";

const VISIBILITY_LEVELS = ["PRIVATE", "WORKSPACE", "PUBLIC"] as const;

type Visibility = (typeof VISIBILITY_LEVELS)[number];
type VisibilityOption = { value: Visibility; label: string };

const VISIBILITY_NAMES = {
	PRIVATE: "Only meeting attendees",
	WORKSPACE: "Anyone in my workspace",
	PUBLIC: "Anyone with the link",
} satisfies { [v in Visibility]: string };

const VISIBILITY_OPTIONS = VISIBILITY_LEVELS.map(value => ({
	value,
	label: VISIBILITY_NAMES[value],
})) satisfies readonly VisibilityOption[];
const VISIBILITY_OPTIONS_WITHOUT_PUBLIC = VISIBILITY_OPTIONS.filter(option => option.value !== "PUBLIC");

type NoteType = NotOther<NonNullable<SendNotesRecordingModalQuery["response"]["node"]>>;
type ViewerType = SendNotesRecordingModalQuery["response"]["viewer"];

function SendNotesRecording({ viewer, note, onClose }: { viewer: ViewerType; note: NoteType; onClose?: () => void }) {
	const featureEnabled = useMeetingRecorderEnabled();

	const initialSelectedUsers = React.useMemo(() => {
		const { emailDomainList, isPersonalType } = viewer.account;

		const noteuserSet: AttendeeAttrs[] = note.noteuserSet
			.filter(u => u.user.isActive)
			.map(noteuser => ({
				...noteuser.user,
				user: noteuser.user,
				lastSeenAt: noteuser.lastSeenAt,
				invitable: false,
				fullName: "",
			}));

		const externalUsersSet: AttendeeAttrs[] =
			note.externalUsers
				?.filter(u => u.isActive)
				.map(externalUser => ({
					...externalUser,
					isExternalInNote: true,
					user: externalUser,
					invitable: !!externalUser.internalAttributes?.invitable,
					fullName: "",
				})) || [];

		const attendees = note.stream.isExternal ? [] : note.event?.attendees;

		const attendeesWithIsExternal =
			attendees
				?.filter(attendee => attendee?.user?.isActive !== false) // Filter out inactive users, but not attendees with no user
				.map(attendee => {
					const isGuest = attendee?.user?.isGuest ?? false;
					const isExternal = !isPersonalType && !emailDomainList.some(domain => attendee?.email.endsWith(`@${domain}`));

					return (
						attendee && {
							...attendee,
							fullName: "",
							isGuest: isGuest,
							isExternal: isExternal && !isGuest,
						}
					);
				}) || [];

		const eventAttendees = compact(attendeesWithIsExternal) as AttendeeAttrs[];
		const mergedAttendees = uniqBy(
			uniqBy(noteuserSet.concat([...eventAttendees, ...externalUsersSet]), item => item.email?.toLowerCase()),
			item => item?.user?.id || item.email?.toLowerCase(),
		);

		return mergedAttendees.filter(u => u.type !== "BOT").filter(u => !u.isExternalInNote);
	}, [viewer, note]);

	const hasMeetingRecordingSummary = Boolean(note.meetingRecordings?.[0]?.hasSummary);
	const hasExternalContacts = initialSelectedUsers.some(u => u.isExternal);
	const { analyticsFingerprint } = React.useContext(BillingContext);
	const [contacts, setContacts] = React.useState<readonly Contact[]>(initialSelectedUsers);
	const [customMessage, setCustomMessage] = React.useState<string | null>(null);
	const [includeNotes, setIncludeNotes] = React.useState(!hasExternalContacts);
	const [includeAiSummary, setIncludeAiSummary] = React.useState(hasMeetingRecordingSummary);
	const [copied, setCopied] = React.useState(false);
	const [sending, setSending] = React.useState(false);

	const analyticsProperties = React.useMemo(
		() => ({
			fingerprint: analyticsFingerprint,
			noteId: note.id,
			noteVersion: note.version,
			meetingRecordingId: note.meetingRecordings?.[0]?.id,
			meetingRecordingLinkId: note.meetingRecordingLink.id,
		}),
		[note, analyticsFingerprint],
	);

	const { visibility } = note.meetingRecordingLink;

	const visibilityOptionsToShow = viewer.account.allowPublicRecap
		? VISIBILITY_OPTIONS
		: VISIBILITY_OPTIONS_WITHOUT_PUBLIC;
	const publicVisibilityButDisabledPublicLinks = !viewer.account.allowPublicRecap && visibility === "PUBLIC";

	const wantsToUpdateVisibilityToPublic = React.useMemo(() => {
		return contacts.some(c => c.isExternal) && visibility !== "PUBLIC" && includeAiSummary;
	}, [contacts, visibility, includeAiSummary]);

	const willUpdateVisibilityToPublic = React.useMemo(() => {
		return wantsToUpdateVisibilityToPublic && viewer.account.allowPublicRecap;
	}, [wantsToUpdateVisibilityToPublic, viewer.account.allowPublicRecap]);

	const willUpdateVisibilityToWorkspace = React.useMemo(() => {
		// If there are contacts that are in the workspace but not in the note, and the visibility is not "WORKSPACE"
		return (
			contacts.some(c => !c.isExternal && !initialSelectedUsers.find(u => u.email === c.email)) &&
			visibility === "PRIVATE" &&
			includeAiSummary
		);
	}, [contacts, initialSelectedUsers, visibility, includeAiSummary]);

	const copyLink = React.useCallback(() => {
		copyToClipboard(note.meetingRecordingLink.url);
		setCopied(true);
		dispatchAnalyticsTrack("[AI Copilot] Recap Link Copied", {
			visibility: note.meetingRecordingLink.visibility,
			...analyticsProperties,
		});

		setTimeout(() => {
			setCopied(false);
		}, 3000);
	}, [note, analyticsProperties]);

	const onContactsChange = React.useCallback((val: OnChangeValue<Contact, true>) => {
		setContacts(val && "length" in val ? val : []);
	}, []);

	const onAddCustomMessage = React.useCallback(() => {
		setCustomMessage("");
	}, []);

	const onCustomMessageChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setCustomMessage(event.target.value);
	}, []);

	const onSendNotesChange = React.useCallback((val: boolean) => {
		setIncludeNotes(val);
	}, []);

	const onSendRecapChange = React.useCallback((val: boolean) => {
		setIncludeAiSummary(val);
	}, []);

	const onClear = React.useCallback(() => {
		setContacts([]);
	}, []);

	const onChangeVisibility = React.useCallback(
		async (selectedOption: VisibilityOption | null) => {
			if (!selectedOption) return;

			const visibility = selectedOption.value;

			try {
				const result = await commitMutationPromise<UpdateRecordingVisibilityMutation>(environment, {
					mutation: graphql`
						mutation SendNotesRecordingModal_updateRecordingVisibilityMutation(
							$noteId: ID!
							$visibility: MeetingRecordingVisibility!
						) @raw_response_type {
							updateMeetingRecordingVisibility(noteId: $noteId, visibility: $visibility) {
								note {
									id
									meetingRecordingLink {
										id
										visibility
									}
								}
							}
						}
					`,
					variables: {
						noteId: note.id,
						visibility,
					},
					optimisticResponse: {
						updateMeetingRecordingVisibility: {
							note: {
								id: note.id,
								meetingRecordingLink: {
									id: note.meetingRecordingLink.id,
									visibility,
								},
							},
						},
					},
				});
				if (!result.updateMeetingRecordingVisibility) {
					throw new Error();
				}
			} catch (e) {
				Sentry.captureException(e);
				toast.error("Something went wrong");
				return;
			}

			toast.success("Visibility updated successfully");
			dispatchAnalyticsTrack("[AI Copilot] Recap Visibility Updated", {
				visibility: visibility,
				...analyticsProperties,
			});
		},
		[note, analyticsProperties],
	);

	const onSendNotes = React.useCallback(async () => {
		if (willUpdateVisibilityToPublic) {
			await onChangeVisibility({ value: "PUBLIC", label: VISIBILITY_NAMES.PUBLIC });
		} else if (willUpdateVisibilityToWorkspace) {
			await onChangeVisibility({ value: "WORKSPACE", label: VISIBILITY_NAMES.WORKSPACE });
		}

		const captchaToken = await recaptcha("send_notes");
		setSending(true);

		const sharedItems = includeNotes && includeAiSummary ? "Note and Recap" : includeNotes ? "Note" : "Recap";
		dispatchAnalyticsTrack(`${sharedItems} Shared`, { method: "email", ...analyticsProperties });

		try {
			const { sendNoteByEmail } = await commitMutationPromise<SendEmailMutation>(environment, {
				mutation: graphql`
					mutation SendNotesRecordingModalSendEmailMutation($input: SendNoteByEmailInput!) {
						sendNoteByEmail(input: $input) {
							ok
						}
					}
				`,
				variables: {
					input: {
						captchaToken,
						message: customMessage ?? "",
						noteId: note.id,
						emails: contacts.map(contact => contact.email),
						includeNotes,
						includeAiSummary: includeAiSummary,
					},
				},
			});

			setSending(false);
			if (!sendNoteByEmail || !sendNoteByEmail.ok) {
				toast.error("You don't have permission to send these notes.");
				return;
			}
			toast.success(`Success, ${sharedItems.toLowerCase()} sent!`);
			onClose?.();
		} catch (e) {
			setSending(false);
			Sentry.captureException(e);
			toast.error("Unable to send email", { title: "Something went wrong" });
		}
	}, [
		contacts,
		customMessage,
		analyticsProperties,
		includeNotes,
		includeAiSummary,
		willUpdateVisibilityToPublic,
		willUpdateVisibilityToWorkspace,
		note,
		onChangeVisibility,
		onClose,
	]);

	return (
		<>
			<Modal.Body className={styles.modalBody}>
				<div className={styles.bodyHeader}>
					<div style={{ display: "flex" }}>
						<div>Send</div>
						<div className={styles.checkbox}>
							<Checkbox id="send-notes-checkbox" checked={includeNotes} onCheckedChange={onSendNotesChange} />
							<label htmlFor="send-notes-checkbox">Notes</label>
						</div>

						{hasMeetingRecordingSummary ? (
							<div className={styles.checkbox}>
								<Checkbox id="send-recap-checkbox" checked={includeAiSummary} onCheckedChange={onSendRecapChange} />
								<label htmlFor="send-recap-checkbox">Recap</label>
								<Tippy
									placement="top"
									className={styles.toolTip}
									content="Includes: the recording, AI summary & highlights, transcript, stats and any clips."
								>
									<span className={styles.helpPopup}>
										<FontAwesomeIcon icon={faInfoCircle} />
									</span>
								</Tippy>
							</div>
						) : featureEnabled ? (
							<Tippy placement="top" className={styles.toolTip} content="There is no Recap for this meeting.">
								<div className={styles.checkbox}>
									<Checkbox disabled id="send-recap-checkbox" />
									<label
										className={cx({ [styles.disabled]: !hasMeetingRecordingSummary })}
										htmlFor="send-recap-checkbox"
									>
										Recap
									</label>
									<span className={styles.helpPopup}>
										<FontAwesomeIcon icon={faInfoCircle} />
									</span>
								</div>
							</Tippy>
						) : null}
						<span style={{ marginLeft: "1rem" }}>via email to:</span>
					</div>
					{contacts.length > 1 && <ButtonLink onClick={onClear}>Clear</ButtonLink>}
				</div>
				<div className={styles.emailListRow}>
					<EmailListInput
						placeholder="Add one or more teammates by email..."
						isMulti
						autoFocus
						externalDiamonds
						value={contacts}
						onChange={onContactsChange}
					/>
					<div>
						<Button
							variant="primary"
							size="normal"
							onClick={onSendNotes}
							disabled={sending || (!includeNotes && !includeAiSummary) || contacts.length === 0}
							style={{ marginLeft: "0.75rem", padding: "0.625rem 1rem" }}
						>
							Send
						</Button>
					</div>
				</div>
				{customMessage === null ? (
					<ButtonLink onClick={onAddCustomMessage} className={styles.addMessage}>
						<FontAwesomeIcon icon={faPlus} style={{ margin: "0 0.5rem" }} />
						Add a custom message (optional)
					</ButtonLink>
				) : (
					<>
						<div className={styles.message}>Custom message (optional)</div>
						<TextArea
							resizable
							placeholder="Write a custom message to be included in your email…"
							onChange={onCustomMessageChange}
							style={{ minHeight: "3.625rem" }}
						/>
					</>
				)}
			</Modal.Body>
			{hasMeetingRecordingSummary && (
				<Modal.Footer className={styles.footer}>
					<div className={styles.footerRow}>
						<div style={{ display: "flex", alignItems: "center" }}>
							<div className={styles.recapText}>Recap:</div>
							<Selector<VisibilityOption>
								condensed
								menuPlacement="auto"
								options={visibilityOptionsToShow}
								value={VISIBILITY_OPTIONS.find(opt => opt.value === visibility)}
								onChange={onChangeVisibility}
								className={styles.visibilitySelector}
							/>
							<div className={styles.recapText}>can access</div>
						</div>
						<ButtonLink onClick={copyLink} disabled={copied}>
							<FontAwesomeIcon icon={copied ? faCheck : faLink} style={{ marginRight: "0.5rem" }} />
							{copied ? "Copied!" : "Copy link"}
						</ButtonLink>
					</div>
					{willUpdateVisibilityToPublic ? (
						<div className={styles.recapAccess}>
							<FontAwesomeIcon icon={faInfoCircle} />
							<div>
								<strong>Access will be updated to “Anyone with the link can view” when you send,</strong> because some
								emails entered above are external to your workspace.
							</div>
						</div>
					) : willUpdateVisibilityToWorkspace ? (
						<div className={styles.recapAccess}>
							<FontAwesomeIcon icon={faInfoCircle} />
							<div>
								<strong>Access will be updated to “Anyone in my workspace” when you send,</strong> because some emails
								entered do not have access to this meeting.
							</div>
						</div>
					) : publicVisibilityButDisabledPublicLinks ? (
						<div className={styles.recapAccess}>
							<FontAwesomeIcon icon={faExclamationCircle} className={styles.warningIcon} />
							Public links are disabled by your workspace admin. Only members within your workspace will be able to view
							the recap.
						</div>
					) : wantsToUpdateVisibilityToPublic && !viewer.account.allowPublicRecap ? (
						<div className={styles.recapAccess}>
							<FontAwesomeIcon icon={faExclamationCircle} className={styles.warningIcon} />
							<div>
								<strong>Your workspace doesn&apos;t allow public access to Recaps.</strong> Some emails entered above
								won&apos;t have access to the recap because they are external to the workspace.
							</div>
						</div>
					) : null}
				</Modal.Footer>
			)}
		</>
	);
}

export default function SendNotesRecordingRenderer({ noteId, onClose }: { noteId: string; onClose?: () => void }) {
	const { viewer, node: note } = useLazyLoadQuery<SendNotesRecordingModalQuery>(
		graphql`
			query SendNotesRecordingModalQuery($id: ID!) {
				viewer {
					account {
						name
						allowPublicRecap
						emailDomainList
						isPersonalType
					}
					user {
						id
						email
						fullName
					}
				}
				node(id: $id) {
					__typename
					... on Note {
						id
						version
						meetingRecordingLink {
							id
							visibility
							url(forCopy: true)
						}
						meetingRecordings {
							id
							hasSummary
						}
						stream {
							isExternal
						}
						event {
							attendees {
								__typename
								email
								fullName
								image
								invitable
								title
								responseStatus
								isOptional
								isOrganizer
								user {
									id
									isGuest
									isActive
								}
							}
						}
						noteuserSet {
							id
							lastSeenAt
							user {
								__typename
								id
								type
								email
								firstName
								fullName
								image
								isActive
								isGuest
								isExternal

								internalAttributes {
									invitable
								}
							}
						}
						externalUsers {
							__typename
							id
							type
							email
							firstName
							fullName
							image
							isActive
							isGuest
							isExternal

							internalAttributes {
								invitable
							}
						}
					}
				}
			}
		`,
		{ id: noteId },
		{ fetchPolicy: "store-and-network" },
	);

	if (!note) return null;
	if (note?.__typename !== "Note") return null;

	return <SendNotesRecording viewer={viewer} note={note} onClose={onClose} />;
}

export function openSendNotesRecordingModal(noteId: string) {
	__globalModalContext?.open(({ close }) => {
		return (
			<React.Suspense
				fallback={
					<Modal onClose={noop}>
						<Modal.Header className={styles.modalHeader} onClose={close}>
							<Modal.Title>Send via email</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<div>Loading...</div>
						</Modal.Body>
					</Modal>
				}
			>
				<Modal onClose={close}>
					<Modal.Header className={styles.modalHeader} onClose={close}>
						<Modal.Title>Send via email</Modal.Title>
					</Modal.Header>
					<SendNotesRecordingRenderer noteId={noteId} onClose={close} />
				</Modal>
			</React.Suspense>
		);
	});
}
